// Mirrors server/src/types.ts response shapes.
export interface Band {
  mbid: string;
  name: string;
  disambiguation: string | null;
  genres: string[];
  imageUrl: string | null;
  imageUrlBig: string | null;
}
export interface UserBand extends Band { topRank: number | null }

export interface BandHistoryEntry {
  id?: string;
  name: string;
  role: string | null;
  url: string | null;
  isCurrent: boolean;
  bandMbid: string | null;
  imageUrl: string | null;
}
export interface UserLink { id?: string; label: string; url: string }
export interface Photo { id: string; url: string }
export interface Wants { instruments: string[]; genres: string[] }

export const SHOWS_PLAYED = ['lt10', '10to50', 'gt50'] as const;
export type ShowsPlayed = (typeof SHOWS_PLAYED)[number];
export const SHOWS_LABEL: Record<ShowsPlayed, string> = { lt10: 'Fewer than 10 shows', '10to50': '10–50 shows', gt50: 'More than 50 shows' };
export const SHOWS_SHORT: Record<ShowsPlayed, string> = { lt10: '<10 shows', '10to50': '10–50 shows', gt50: '50+ shows' };

interface ProfileCommon {
  id: string;
  displayName: string;
  photoUrl: string | null;
  photos: Photo[];
  backgroundUrl: string | null;
  looking: boolean;
  showsPlayed: ShowsPlayed | null;
  bio: string | null;
  city: string | null;
  instruments: string[];
  genres: string[];
  wants: Wants;
  bands: UserBand[];
  bandHistory: BandHistoryEntry[];
  links: UserLink[];
  likes: number;
}

export interface Profile extends ProfileCommon {
  birthdate: string | null;
  age: number | null;
  ageHidden: boolean;
  lat: number | null;
  lng: number | null;
  email: string | null;
}

export interface PublicProfile extends ProfileCommon {
  age: number | null;
  likedByMe: boolean;
  interestSent: boolean;
  followers: number;
  following: number;
  followedByMe: boolean;
  followsMe: boolean;
}

export interface PostMedia {
  id: string;
  kind: 'image' | 'video' | 'audio';
  url: string;
  contentType: string;
  bytes: number;
  width: number | null;
  height: number | null;
  durationMs: number | null;
}
export interface Post {
  id: string;
  author: { id: string; displayName: string; photoUrl: string | null };
  body: string;
  media: PostMedia[];
  createdAt: number;
  mine: boolean;
}
export interface PostLimits {
  maxMedia: number;
  maxBody: number;
  maxDailyUploads: number;
  media: Record<'image' | 'video' | 'audio', { enabled: boolean; maxBytes: number; types: string[]; maxDurationMs?: number }>;
}

export interface BandSearchHit {
  mbid: string;
  name: string;
  disambiguation: string | null;
  type: string | null;
  country: string | null;
  imageUrl: string | null;
}

export interface SearchCriteria {
  minAge: number | null;
  maxAge: number | null;
  city: string | null;
  lat: number | null;
  lng: number | null;
  radiusKm: number;
  bandSource: 'profile' | 'custom';
  bands: { mbid: string; top: boolean }[];
  instruments: string[];
  showsPlayed: ShowsPlayed[];
}
export interface SavedSearch extends SearchCriteria {
  id: string;
  name: string;
  createdAt: number;
  updatedAt: number;
  lastRunAt: number | null;
}

export interface MatchResult {
  user: PublicProfile;
  score: number;
  distanceKm: number | null;
  sharedBands: Band[];
  similarBands: { yours: Band; theirs: Band; score: number }[];
}

export interface GeoResult { label: string; lat: number; lng: number }

export interface ChatMessage { id: string; roomId: string; senderId: string; body: string; createdAt: number; clientTag?: string }
export interface ChatRoom {
  roomId: string;
  peer: { id: string; displayName: string; photoUrl: string | null };
  lastMessageAt: number;
  lastBody: string | null;
  unread: boolean;
}

export interface Activity {
  id: string;
  kind: 'like' | 'interest' | 'follow';
  actor: { id: string; displayName: string; photoUrl: string | null };
  createdAt: number;
}

export interface Settings {
  email: string | null;
  hasPassword: boolean;
  pushEnabled: boolean;
  pushDevices: number;
  identities: { provider: string; display: string | null; email: string | null }[];
  providers: string[];
}
